import {
  type FaxConnection,
  FaxConnectionSchema,
  FaxReceiptSchema,
  FaxRecipientSchema,
  FaxReplyPreparedSchema,
  type FaxReplyRequest,
  FaxReplySchema,
  FaxTransmissionSchema,
} from "@emr/contracts";
import { z } from "zod";

export const FaxMemberSchema = z.object({
  userId: z.string(),
  displayName: z.string().nullable(),
  role: z.string(),
  active: z.boolean(),
});
export type FaxChartSource = {
  kind: "document" | "incoming_document" | "clinic_document" | "referral_package";
  id: string;
  version?: number;
};

/** An HTTP response was received; network failures remain their original errors. */
export class FaxRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "FaxRequestError";
  }
}

export function createFaxesClient(
  request: (path: string, init?: RequestInit) => Promise<Response>,
) {
  const checked = async (response: Response) => {
    if (!response.ok) {
      const body: {
        error?: { message?: unknown; code?: unknown };
        message?: unknown;
      } | null = await response.json().catch(() => null);
      const message =
        typeof body?.error?.message === "string"
          ? body.error.message
          : typeof body?.message === "string"
            ? body.message
            : "Fax request failed. Refresh before retrying.";
      throw new FaxRequestError(
        message,
        response.status,
        typeof body?.error?.code === "string" ? body.error.code : undefined,
      );
    }
    return response;
  };
  const json = async <T>(path: string, schema: z.ZodType<T>, body?: unknown) =>
    schema.parse(
      await (
        await checked(
          await request(
            `/v1/faxes${path}`,
            body === undefined
              ? undefined
              : {
                  method: "POST",
                  headers: { "content-type": "application/json" },
                  body: JSON.stringify(body),
                },
          ),
        )
      ).json(),
    );
  const version = (expectedVersion: number, idempotencyKey: string = crypto.randomUUID()) => ({
    expectedVersion,
    idempotencyKey,
  });
  return {
    sendReferralFax: async (
      referralId: string,
      input: { expectedVersion: number; idempotencyKey: string },
    ) =>
      FaxTransmissionSchema.parse(
        await (
          await checked(
            await request(`/v1/referrals/${encodeURIComponent(referralId)}/fax`, {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify(input),
            }),
          )
        ).json(),
      ),
    getFaxSettings: () => json("/settings", FaxConnectionSchema.nullable()),
    saveFaxSettings: (
      input: Pick<
        FaxConnection,
        | "credentialPrefix"
        | "inboundEnabled"
        | "outboundEnabled"
        | "faxAdminId"
        | "escalationPhysicianId"
      > & { expectedVersion: number; idempotencyKey: string },
    ) => json("/settings", FaxConnectionSchema, input),
    listFaxMembers: () => json("/members", z.array(FaxMemberSchema)),
    listFaxRecipients: () => json("/recipients", z.array(FaxRecipientSchema)),
    saveFaxRecipient: (input: {
      name: string;
      faxNumber: string;
      id?: string;
      expectedVersion?: number;
      idempotencyKey: string;
    }) => json("/recipients", FaxRecipientSchema, input),
    listFaxReceipts: (search = "") =>
      json(`?direction=received&search=${encodeURIComponent(search)}`, z.array(FaxReceiptSchema)),
    listFaxTransmissions: (search = "", patientId?: string) =>
      json(
        `?direction=sent&search=${encodeURIComponent(search)}${patientId ? `&patientId=${encodeURIComponent(patientId)}` : ""}`,
        z.array(FaxTransmissionSchema),
      ),
    getFaxReceipt: (id: string) => json(`/receipts/${encodeURIComponent(id)}`, FaxReceiptSchema),
    retryFaxReceipt: (id: string, expectedVersion: number, key?: string) =>
      json(
        `/receipts/${encodeURIComponent(id)}/retry`,
        FaxReceiptSchema,
        version(expectedVersion, key),
      ),
    getFaxTransmission: (id: string) =>
      json(`/transmissions/${encodeURIComponent(id)}`, FaxTransmissionSchema),
    async prepareFaxPacket(input: {
      recipientId: string;
      patientId?: string;
      title: string;
      coverNote: string;
      files: File[];
      sources?: FaxChartSource[];
      idempotencyKey: string;
    }) {
      const files = await Promise.all(
        input.files.map(async (file) => {
          const bytes = new Uint8Array(await file.arrayBuffer());
          let binary = "";
          for (let index = 0; index < bytes.length; index += 0x8000)
            binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
          return {
            name: file.name,
            mimeType:
              file.type ||
              (/\.pdf$/i.test(file.name)
                ? "application/pdf"
                : /\.png$/i.test(file.name)
                  ? "image/png"
                  : "image/jpeg"),
            base64: btoa(binary),
          };
        }),
      );
      return json("/packets", FaxTransmissionSchema, { ...input, files });
    },
    faxCommand: (
      id: string,
      operation: "queue" | "retry" | "cancel",
      expectedVersion: number,
      key?: string,
    ) =>
      json(
        `/transmissions/${encodeURIComponent(id)}/${operation}`,
        FaxTransmissionSchema,
        version(expectedVersion, key),
      ),
    resolveFaxReceipt: (
      id: string,
      input: {
        expectedVersion: number;
        idempotencyKey: string;
        disposition: "file" | "nonpatient" | "misdirected";
        reason: string;
        groups?: { pages: number[]; patientId: string; ownerId?: string }[];
      },
    ) => json(`/receipts/${encodeURIComponent(id)}/resolve`, FaxReceiptSchema, input),
    /** Replies already faxed back for a received fax (EXECDEV-207), newest first. */
    listFaxReplies: (receiptId: string) =>
      json(`/receipts/${encodeURIComponent(receiptId)}/replies`, z.array(FaxReplySchema)),
    /**
     * Draws the marks onto the received fax's safe copy and prepares the exact packet as a
     * draft; queue it with faxCommand(transmission.id, "queue", …) after reviewing it.
     */
    prepareFaxReply: (receiptId: string, input: FaxReplyRequest) =>
      json(`/receipts/${encodeURIComponent(receiptId)}/replies`, FaxReplyPreparedSchema, input),
    downloadFax: async (id: string, direction: "received" | "sent") =>
      (
        await checked(
          await request(
            `/v1/faxes/${direction === "received" ? "receipts" : "transmissions"}/${encodeURIComponent(id)}/pdf`,
          ),
        )
      ).blob(),
  };
}
export type FaxesClient = ReturnType<typeof createFaxesClient>;
